(() => {
  if (customElements.get('cave-predictive-search')) return;
  class CavePredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.suggestions = this.querySelector('[data-cave-suggestions]');
      this.status = this.querySelector('[role="status"]');
      this.welcome = this.querySelector('[data-cave-search-welcome]');
      this.footer = this.querySelector('[data-cave-search-footer]');
      this.allLink = this.querySelector('[data-cave-search-all]');
      this.cache = new Map();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input.addEventListener('input', () => this.queue(), options);
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2 && this.suggestions.hidden) this.queue();
      }, options);
      this.addEventListener('keydown', event => {
        this.selectingSuggestion = false;
        if (event.key === 'Escape') { this.close(); this.input.focus(); }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.suggestions.hidden) return;
        const links = [...this.suggestions.querySelectorAll('a')];
        if (!links.length) return;
        const index = links.indexOf(document.activeElement);
        const next = event.key === 'ArrowDown' ? Math.min(index + 1, links.length - 1) : index - 1;
        event.preventDefault();
        (next < 0 ? this.input : links[next]).focus();
      }, options);
      document.addEventListener('pointerdown', event => {
        this.selectingSuggestion = this.contains(event.target);
        if (!this.contains(event.target)) this.close();
      }, { ...options, capture: true });
      const endPointer = () => { this.selectingSuggestion = false; };
      document.addEventListener('click', endPointer, options);
      document.addEventListener('pointercancel', endPointer, options);
      this.addEventListener('focusout', event => {
        // Touch browsers may blur the field without focusing the tapped link.
        // The dialog remains open when the keyboard is dismissed.
        if (!this.closest('dialog') && !this.selectingSuggestion && !this.contains(event.relatedTarget)) this.close();
      }, options);
    }
    disconnectedCallback() { this.close(); this.controller?.abort(); }
    close() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.suggestions.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.status.textContent = '';
      this.removeAttribute('aria-busy');
    }
    queue() {
      this.close();
      const query = this.input.value.trim();
      if (this.welcome) this.welcome.hidden = query.length >= 2;
      if (this.footer) this.footer.hidden = query.length < 2;
      if (query.length < 2) return;
      const url = new URL(this.querySelector('form').action, location.origin);
      url.searchParams.set('q', query);
      if (this.allLink) {
        this.allLink.href = url.href;
        this.allLink.textContent = `View all results for “${query}”`;
      }
      this.status.textContent = 'Searching…';
      this.setAttribute('aria-busy', 'true');
      this.timer = setTimeout(() => this.search(query), 150);
    }
    safeURL(value) {
      try {
        const url = new URL(value, location.origin);
        return url.origin === location.origin && ['https:', 'http:'].includes(url.protocol) ? url.href : null;
      } catch { return null; }
    }
    money(value) {
      if (value === undefined || value === null || value === '' || !Number.isFinite(Number(value))) return '';
      try { return new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' }).format(Number(value)); }
      catch { return ''; }
    }
    group(key, title, items, limit) {
      const section = document.createElement('section');
      section.className = `cave-predictive__group cave-predictive__${key}`;
      const heading = document.createElement('h3');
      heading.className = 'cave-predictive__heading';
      heading.textContent = title;
      section.append(heading);
      const list = document.createElement('ul');
      let count = 0;
      for (const item of items.slice(0, limit)) {
        const href = this.safeURL(item.url);
        if (!href) continue;
        const row = document.createElement('li');
        const link = document.createElement('a');
        link.href = href;
        const name = item.title || item.text;
        if (!name) continue;
        if (key === 'products') {
          link.className = 'cave-predictive__product';
          const source = item.featured_image?.url || item.image;
          if (source) {
            try {
              const imageURL = new URL(source, location.origin);
              if (imageURL.protocol === 'https:' && (imageURL.origin === location.origin || imageURL.hostname === 'cdn.shopify.com')) {
                imageURL.searchParams.set('width', '160');
                const image = document.createElement('img');
                image.src = imageURL.href; image.alt = ''; image.width = 72; image.height = 88; image.decoding = 'async';
                link.append(image);
              }
            } catch { /* Product names remain usable if an image is unavailable. */ }
          }
          const details = document.createElement('span');
          details.className = 'cave-predictive__details';
          const label = document.createElement('span'); label.textContent = name; details.append(label);
          const price = this.money(item.price);
          if (price) { const p = document.createElement('span'); p.className = 'cave-predictive__price'; p.textContent = price; details.append(p); }
          if (item.available === false) { const sold = document.createElement('span'); sold.className = 'cave-predictive__sold'; sold.textContent = 'Sold out'; details.append(sold); }
          link.append(details);
        } else {
          link.textContent = name;
        }
        row.append(link); list.append(row); count++;
      }
      if (!count) return null;
      section.append(list);
      return { section, count };
    }
    async search(query) {
      const request = this.request = new AbortController();
      try {
        let results = this.cache.get(query);
        if (!results) {
          const url = new URL(this.dataset.endpoint + '.json', location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'query,product,collection,page,article');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'each');
          url.searchParams.set('resources[options][unavailable_products]', 'show');
          const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Suggestions unavailable');
          results = (await response.json()).resources.results;
          if (this.cache.size >= 30) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(query, results);
        }
        if (request !== this.request || this.input.value.trim() !== query) return;
        const fragment = document.createDocumentFragment();
        let count = 0;
        const collections = [...(results.collections || [])].sort((a, b) => Number(b.title?.toLocaleLowerCase() === query.toLocaleLowerCase()) - Number(a.title?.toLocaleLowerCase() === query.toLocaleLowerCase()));
        for (const [key, title, items, limit] of [
          ['collections', 'Shop by collection', collections, 3],
          ['products', 'Wines & products', results.products || [], 4],
          ['queries', 'Suggested searches', results.queries || [], 3],
          ['guides', 'Guides & more', [...(results.articles || []), ...(results.pages || [])], 2]
        ]) {
          const group = this.group(key, title, items, limit);
          if (group) { fragment.append(group.section); count += group.count; }
        }
        this.suggestions.replaceChildren(fragment);
        this.suggestions.hidden = count === 0;
        this.input.setAttribute('aria-expanded', String(count > 0));
        this.status.textContent = count ? `${count} suggestions` : 'No suggestions yet. Try another spelling or view all search results.';
        this.removeAttribute('aria-busy');
      } catch (error) {
        if (request !== this.request) return;
        this.suggestions.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.status.textContent = 'Suggestions are unavailable. You can still search all results.';
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cave-predictive-search', CavePredictiveSearch);
  class CaveSearchPanel extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      document.querySelectorAll('[data-show-search-trigger]').forEach(trigger => {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('tabindex', '0');
        trigger.setAttribute('aria-label', 'Search Cave');
        trigger.setAttribute('aria-haspopup', 'dialog');
      });
      const activate = event => {
        const trigger = event.target.closest?.('[data-show-search-trigger]');
        if (!trigger || (event.type === 'keydown' && !['Enter', ' '].includes(event.key))) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
      };
      document.addEventListener('click', activate, { ...options, capture: true });
      document.addEventListener('keydown', activate, { ...options, capture: true });
      this.querySelector('[data-cave-search-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }, { ...options, capture: true });
      this.dialog.addEventListener('close', () => {
        document.body.style.overflow = this.previousOverflow || '';
        this.querySelector('cave-predictive-search').close();
        document.querySelectorAll('[data-show-search-trigger]').forEach(el => el.classList.remove('is-active'));
        this.opener?.focus();
      }, options);
      window.addEventListener('pagehide', () => this.close(), options);
    }
    disconnectedCallback() { this.close(); this.controller?.abort(); }
    open(trigger) {
      if (this.dialog.open) return;
      this.opener = trigger || document.activeElement;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      const component = this.querySelector('cave-predictive-search');
      component.input.focus({ preventScroll: true });
      component.queue();
    }
    close() { if (this.dialog.open) this.dialog.close(); }
  }
  customElements.define('cave-search-panel', CaveSearchPanel);
})();
